import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useFavicon } from '../hooks/useFavicon';

interface ThemeContextType {
  isDarkMode: boolean;
  toggleDarkMode: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const ThemeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isDarkMode, setIsDarkMode] = useState(() => {
    // Check localStorage first, default to light mode if not set
    const saved = localStorage.getItem('anglopi-dark-mode');
    if (saved !== null) {
      return JSON.parse(saved);
    }
    return false; // Default to light mode
  });

  // Use the favicon hook to update favicon based on system theme preference
  useFavicon();

  useEffect(() => {
    // Save to localStorage when theme changes
    localStorage.setItem('anglopi-dark-mode', JSON.stringify(isDarkMode));
    
    // Update CSS custom properties
    const root = document.documentElement;
    if (isDarkMode) {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
  }, [isDarkMode]);

  const toggleDarkMode = () => {
    setIsDarkMode((prev: boolean) => !prev);
  };

  return (
    <ThemeContext.Provider value={{ isDarkMode, toggleDarkMode }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
